import React from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { ArrowDropDownRounded } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";

import SteamSignInButton from "../../auth/SteamSignInButton";
import PlaySafeSignInButton from "../../auth/PlaySafeSignInButton";
import { useAuthState } from "../../app/AuthStateContext";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import usePublicStats from "../../public-site/usePublicStats";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "../../public-site/discordInviteUrl";
import DiscordIcon from "../../public-site/DiscordIcon";
import utils from "../../../universal/utils";
import { colors } from "../dark-hero/colors";
import { ULTRA_WIDE_MEDIA } from "../dark-hero/breakpoints";
import { CENTERED_GLOW_SX, OVERFLOW_X_CLIP_SX } from "../dark-hero/layoutGuards";
import MatchNightSection from "../dark-hero/MatchNightSection";
import ServerMapSection from "../dark-hero/ServersMapSection";
import {
  DISCORD_CTA_LABEL,
  HEADLINE,
  HERO_BUILDOUT,
  HERO_SUBHEAD,
  STEAM_CTA_LABEL,
  STEAM_PERMISSION_LINE,
  TEAM_NOTE,
  TEAM_SIGNATURE,
} from "./honest-buildContent";
import HeroPillars from "./HeroPillars";
import StatsRow from "./StatsRow";
import NextMatchNight from "./NextMatchNight";
import HowThisWorksNow from "./HowThisWorksNow";
import TooGoodToBeTrue from "./TooGoodToBeTrue";
import RecentSignupNotifications from "./RecentSignupNotifications";

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HERO_BG = "#080808";
const HERO_PHOTO_URL =
  "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/backgrounds/public-site-ouro-hero-background.jpeg";

const HERO_COPY_ENTER = {
  initial: { y: 18, opacity: 0, filter: "blur(8px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};
const HERO_COPY_ENTER_TRANSITION = {
  duration: 0.7,
  delay: 0.25,
  ease: [0.22, 1, 0.36, 1],
};

const PAGE_ROOT_SX = {
  ...OVERFLOW_X_CLIP_SX,
  minHeight: "100svh",
  backgroundColor: HERO_BG,
};

const HERO_SECTION_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  ...OVERFLOW_X_CLIP_SX,
  minHeight: "100svh",
  backgroundColor: HERO_BG,
  isolation: "isolate",
};

const HERO_CONTENT_SX = {
  position: "relative",
  zIndex: 5,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  flex: 1,
  ...OVERFLOW_X_CLIP_SX,
  mx: "auto",
  px: { xs: 2, sm: 2.5, md: 3 },
  py: { xs: 2, md: 3 },
  pt: { xs: 9, md: 11 },
  textAlign: "center",
  width: "100%",
};

const HERO_HEADLINE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "1.65rem", sm: "2.1rem", md: "2.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.045em",
  lineHeight: 1.08,
  color: colors.text.primary,
  maxWidth: "22ch",
  textTransform: "none",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "3.25rem",
  },
};

const HERO_CTA_SHARED_SX = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  height: { xs: "2.75rem", md: "3.15rem" },
  minHeight: { xs: "2.75rem", md: "3.15rem" },
  px: { xs: "0.75rem", md: "1rem" },
  borderRadius: { xs: "12px", md: "14px" },
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
  letterSpacing: "0.02em",
  fontFamily: "'Inter', system-ui, sans-serif",
  "& .MuiButton-startIcon": { mr: 1, ml: 0 },
};

const PLAY_NOW_BUTTON_SX = {
  ...HERO_CTA_SHARED_SX,
  border: "1px solid rgba(255,210,120,0.24)",
  background: "linear-gradient(180deg, #f7cb79 0%, #f0b458 42%, #de8f2c 100%)",
  color: "#120c04",
  boxShadow: "0 10px 30px rgba(232,163,61,0.24)",
  "&:hover": {
    filter: "brightness(1.04)",
    boxShadow: "0 14px 36px rgba(232,163,61,0.32)",
  },
};

const STEAM_GHOST_BUTTON_SX = {
  ...HERO_CTA_SHARED_SX,
  overflow: "visible",
  border: `1px solid ${colors.primary.main}`,
  background: "transparent",
  backgroundColor: "transparent",
  backgroundImage: "none",
  color: colors.primary.main,
  boxShadow: "none",
  "&.MuiButton-contained": {
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: colors.primary.main,
    boxShadow: "none",
  },
  "&:hover": {
    backgroundColor: colors.accent.subtle,
    backgroundImage: "none",
    border: `1px solid ${colors.primary.main}`,
    boxShadow: "none",
    filter: "none",
  },
  "&:focus-visible, &.Mui-focusVisible": {
    outline: `2px solid ${colors.primary.main}`,
    outlineOffset: "3px",
    boxShadow: "none",
  },
};

const HERO_SSO_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: "stretch",
  justifyContent: "center",
  gap: { xs: "0.65rem", md: "1rem" },
  width: "fit-content",
  maxWidth: { xs: "18rem", md: "none" },
  mx: "auto",
};

const HERO_SSO_COL_SX = {
  width: { xs: "100%", md: "14.5rem" },
  maxWidth: { xs: "18rem", md: "14.5rem" },
};

const CONTINUE_PLAYSAFE_SX = {
  width: "100%",
  height: { xs: "2.5rem", md: "2.75rem" },
  minHeight: { xs: "2.5rem", md: "2.75rem" },
  px: { xs: "0.75rem", md: "0.9rem" },
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.28)",
  color: "rgba(255,255,255,0.92)",
  backgroundColor: "rgba(0,0,0,0.35)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderColor: "rgba(255,255,255,0.4)",
  },
  "& .playsafe-button-inner span": {
    fontSize: { xs: "0.75rem", md: "0.8125rem" },
  },
};

function HeroPhotoBackground() {
  return (
    <Box aria-hidden sx={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}>
      <Box
        component="img"
        src={HERO_PHOTO_URL}
        alt=""
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: { xs: "70% 40%", md: "62% 38%" },
          filter: "saturate(1.04) contrast(1.04)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(
              102deg,
              rgba(5,5,5,0.96) 0%,
              rgba(5,5,5,0.82) 32%,
              rgba(5,5,5,0.4) 58%,
              transparent 78%
            )
          `,
        }}
      />
    </Box>
  );
}

function TeamNote() {
  return (
    <Box
      sx={{
        maxWidth: "40rem",
        mx: "auto",
        mt: { xs: 1.25, md: 1.5 },
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: "0.8rem", md: "0.85rem" },
          lineHeight: 1.45,
          color: "rgba(255,255,255,0.78)",
        }}
      >
        {TEAM_NOTE}
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontSize: "0.72rem",
          fontWeight: 600,
          color: colors.text.secondary,
        }}
      >
        {TEAM_SIGNATURE}
      </Typography>
    </Box>
  );
}

export default function HonestBuildHomePage({ catalog, templateReady = true }) {
  const { googleResponse, onFailure, isAuthenticated } = useAuthState();
  const { user } = catalog;
  const track = useTemplateTrack();
  const stats = usePublicStats();
  const [googleSsoOpen, setGoogleSsoOpen] = React.useState(false);
  const [discordUrl, setDiscordUrl] = React.useState(DEFAULT_DISCORD_INVITE_URL);
  const isUltraWide = useMediaQuery(ULTRA_WIDE_MEDIA);
  const isHeroMdUp = useMediaQuery("(min-width:900px)");
  const heroGoogleWidth = isUltraWide ? 360 : isHeroMdUp ? 280 : 250;

  React.useEffect(() => {
    if (!templateReady) return;
    track("homepage_view");
  }, [templateReady, track]);

  React.useEffect(() => {
    let cancelled = false;
    fetchPublicDiscordInviteUrl(utils.getEdgeBasePath())
      .then((url) => {
        if (!cancelled) setDiscordUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (isAuthenticated && user?.email) {
    return <Redirect to="/lobby" />;
  }

  return (
    <Box sx={PAGE_ROOT_SX}>
      <RecentSignupNotifications enabled={templateReady} />
      <Box sx={HERO_SECTION_SX}>
        <HeroPhotoBackground />
        <Box sx={HERO_CONTENT_SX}>
          <MotionTypography
            component="h1"
            {...HERO_COPY_ENTER}
            transition={HERO_COPY_ENTER_TRANSITION}
            sx={HERO_HEADLINE_SX}
          >
            {HEADLINE}
          </MotionTypography>
          <Typography
            sx={{
              mt: { xs: 1, md: 1.25 },
              maxWidth: "36rem",
              mx: "auto",
              fontSize: { xs: "0.85rem", md: "0.95rem" },
              lineHeight: 1.45,
              color: colors.text.secondary,
            }}
          >
            {HERO_SUBHEAD}
          </Typography>
          <TeamNote />
          <Box
            sx={{
              mt: { xs: 1.5, md: 2 },
              width: "100%",
              maxWidth: "72rem",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.15fr) minmax(0, 0.95fr)" },
              gap: { xs: 2, md: 3 },
              alignItems: "start",
            }}
          >
            <Box>
              <HeroPillars />
            </Box>
            <Box>
              <NextMatchNight preferredRegion={stats?.visitor?.matchNightRegion} />
              <Typography
                sx={{
                  mt: 1,
                  fontSize: { xs: "0.72rem", md: "0.78rem" },
                  lineHeight: 1.45,
                  color: colors.text.muted,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {HERO_BUILDOUT}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: 1.5, md: 2 }, width: "100%" }}>
            <StatsRow stats={stats} />
          </Box>
          <Box sx={{ mt: { xs: 1.5, md: 2.25 }, width: "100%" }}>
            <Box sx={HERO_SSO_ROW_SX}>
              <Box sx={HERO_SSO_COL_SX}>
                <Button
                  component="a"
                  href={discordUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<DiscordIcon size={18} />}
                  onClick={() => track("discord_clicked", { surface: "hero" })}
                  sx={PLAY_NOW_BUTTON_SX}
                >
                  {DISCORD_CTA_LABEL}
                </Button>
              </Box>
              <Box sx={HERO_SSO_COL_SX}>
                <SteamSignInButton
                  label={STEAM_CTA_LABEL}
                  logoSize={20}
                  showAssurance={false}
                  surface="hero"
                  fullWidth
                  sx={STEAM_GHOST_BUTTON_SX}
                />
              </Box>
            </Box>
            <Typography
              sx={{
                mt: 1,
                maxWidth: "28rem",
                mx: "auto",
                fontSize: { xs: "0.68rem", md: "0.72rem" },
                lineHeight: 1.4,
                color: colors.text.muted,
                textAlign: "center",
              }}
            >
              {STEAM_PERMISSION_LINE}
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Box
                component="button"
                type="button"
                aria-expanded={googleSsoOpen}
                aria-controls="hero-google-sso-panel"
                onClick={() => setGoogleSsoOpen((open) => !open)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: colors.text.secondary,
                  "&:hover": { color: colors.text.primary },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Other sign-in, not required
                </Typography>
                <ArrowDropDownRounded
                  aria-hidden
                  sx={{
                    fontSize: "1.2rem",
                    transform: googleSsoOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                />
              </Box>
              <Box
                id="hero-google-sso-panel"
                aria-hidden={!googleSsoOpen}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <AnimatePresence initial={false}>
                  {googleSsoOpen ? (
                    <MotionBox
                      key="hero-google-sso"
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      sx={{
                        pt: 0.75,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ width: { xs: heroGoogleWidth, sm: 220 } }}>
                        <PlaySafeSignInButton
                          label="Sign in with PlaySafe ID"
                          surface="hero"
                          fullWidth
                          logoHeight={18}
                          sx={CONTINUE_PLAYSAFE_SX}
                        />
                      </Box>
                      <Box
                        onClick={() =>
                          track("sso_google_clicked", { provider: "google", surface: "hero" })
                        }
                        sx={{ colorScheme: "light" }}
                      >
                        <GoogleLogin
                          shape="pill"
                          size="large"
                          theme="filled_black"
                          text="continue_with"
                          width={heroGoogleWidth}
                          onSuccess={googleResponse}
                          onError={onFailure}
                        />
                      </Box>
                    </MotionBox>
                  ) : null}
                </AnimatePresence>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: "-1px",
            left: 0,
            width: "100%",
            height: "140px",
            pointerEvents: "none",
            zIndex: 2,
            background: `linear-gradient(to bottom, rgba(8,8,8,0) 0%, ${HERO_BG} 100%)`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-120px",
            ...CENTERED_GLOW_SX,
            height: "320px",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 2,
            background:
              "radial-gradient(ellipse at center, rgba(232,163,61,0.12) 0%, transparent 72%)",
            filter: "blur(60px)",
          }}
        />
      </Box>
      <HowThisWorksNow />
      <TooGoodToBeTrue />
      <MatchNightSection />
      <ServerMapSection />
    </Box>
  );
}

HonestBuildHomePage.propTypes = {
  catalog: PropTypes.shape({
    user: PropTypes.object,
  }),
  templateReady: PropTypes.bool,
};
